class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleVariantTriggerClick = this.handleVariantTriggerClick.bind(this);
    this.handleVariantSelectChange = this.handleVariantSelectChange.bind(this);
    this.handleCardMouseEnter = this.handleCardMouseEnter.bind(this);
    this.handleCardMouseLeave = this.handleCardMouseLeave.bind(this);
    this.handleCardFocusIn = this.handleCardFocusIn.bind(this);
    this.handleCardFocusOut = this.handleCardFocusOut.bind(this);
    this.handleWindowLoad = this.handleWindowLoad.bind(this);
    this.handleHoverImageIntersection = this.handleHoverImageIntersection.bind(this);
    this.handleDropdownButtonClick = this.handleDropdownButtonClick.bind(this);
    this.handleDropdownButtonKeydown = this.handleDropdownButtonKeydown.bind(this);
    this.handleDropdownPanelClick = this.handleDropdownPanelClick.bind(this);
    this.handleDropdownPanelKeydown = this.handleDropdownPanelKeydown.bind(this);
    this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    this.handleDropdownReposition = this.handleDropdownReposition.bind(this);
  }

  connectedCallback() {
    if (this._listenerAdded) return;
    this._listenerAdded = true;

    this.forms = Array.from(this.querySelectorAll('form[action]'));
    this.variantSelect = this.querySelector('[data-card4-variant-select]');
    this.variantTriggers = Array.from(this.querySelectorAll('[data-card4-variant-trigger]'));
    this.moreLink = this.querySelector('[data-card4-more-link]');
    this.priceElement = this.querySelector('[data-card4-price]');
    this.compareElement = this.querySelector('[data-card4-compare]');
    this.compareValueElement = this.querySelector('[data-card4-compare-value]');
    this.mediaElement = this.querySelector('[data-product-card-media]');
    this.imageHoverTarget = this.mediaElement?.closest('a') || this.mediaElement;
    this.imageElement = this.mediaElement?.querySelector('img') || null;
    this.defaultImageState = this.imageElement
      ? {
          src: this.imageElement.getAttribute('src') || '',
          srcset: this.imageElement.getAttribute('srcset') || '',
          alt: this.imageElement.getAttribute('alt') || ''
        }
      : null;
    this.hoverImageSrc = this.mediaElement?.dataset.productCardHoverImage || '';
    this.hoverImageAlt =
      this.mediaElement?.dataset.productCardHoverImageAlt || this.defaultImageState?.alt || '';
    this.hoverImageElement = null;
    this.hoverImageLoadPromise = null;
    this.hoverImageFailed = false;
    this.isHoverImageActive = false;
    this.enableVariantImageSwap = this.dataset.card4VariantImageEnabled === 'true';
    this.hasHoverImageSupport = Boolean(this.mediaElement && this.imageElement && this.hoverImageSrc);

    this.forms.forEach((form) => {
      form.addEventListener('submit', this.handleFormSubmit);
    });

    this.variantTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleVariantTriggerClick);
    });

    this.variantSelect?.addEventListener('change', this.handleVariantSelectChange);

    if (this.hasHoverImageSupport) {
      this.mediaElement.classList.add('has-hover-image');
      this.mediaElement.addEventListener('mouseenter', this.handleCardMouseEnter);
      this.mediaElement.addEventListener('mouseleave', this.handleCardMouseLeave);
      this.imageHoverTarget?.addEventListener('focusin', this.handleCardFocusIn);
      this.imageHoverTarget?.addEventListener('focusout', this.handleCardFocusOut);
      this.setupHoverImagePreload();
    }

    if (this.variantSelect) {
      this.setupVariantDropdown();

      const initialVariant = this.getSelectedVariantOption();
      if (initialVariant) {
        this.syncVariantState(initialVariant, { updateImage: false });
      }
    } else {
      this.forms.forEach((form) => {
        this.updateForms(form);
      });
    }
  }

  disconnectedCallback() {
    if (!this._listenerAdded) return;

    this.forms?.forEach((form) => {
      form.removeEventListener('submit', this.handleFormSubmit);
    });

    this.variantTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleVariantTriggerClick);
    });

    this.variantSelect?.removeEventListener('change', this.handleVariantSelectChange);

    this.destroyVariantDropdown();

    if (this.hasHoverImageSupport) {
      this.mediaElement?.removeEventListener('mouseenter', this.handleCardMouseEnter);
      this.mediaElement?.removeEventListener('mouseleave', this.handleCardMouseLeave);
      this.imageHoverTarget?.removeEventListener('focusin', this.handleCardFocusIn);
      this.imageHoverTarget?.removeEventListener('focusout', this.handleCardFocusOut);
      window.removeEventListener('load', this.handleWindowLoad);
      this.destroyHoverImageObserver();
      this.cancelHoverImagePreload();
    }

    this._listenerAdded = false;
  }

  getSelectedVariantOption() {
    if (!this.variantSelect) return null;

    return (
      this.variantSelect.selectedOptions?.[0] ||
      this.variantSelect.querySelector('option[selected]') ||
      this.variantSelect.options?.[0] ||
      null
    );
  }

  getVariantSourceValue(source, attributeNames = [], datasetKeys = []) {
    if (!source) return '';

    for (const key of datasetKeys) {
      const value = source.dataset?.[key];
      if (value != null && `${value}` !== '') return `${value}`;
    }

    for (const attributeName of attributeNames) {
      if (typeof source.getAttribute !== 'function') continue;

      const value = source.getAttribute(attributeName);
      if (value != null && value !== '') return value;
    }

    if ('value' in source && source.value != null && `${source.value}` !== '') {
      return `${source.value}`;
    }

    return '';
  }

  getVariantId(source) {
    return this.getVariantSourceValue(source, ['data-variant-id'], ['variantId']);
  }

  getVariantAvailability(source) {
    return this.getVariantSourceValue(source, ['data-variant-available'], ['variantAvailable']) === 'true';
  }

  getVariantMinimumQuantity(source) {
    if (!window.CartApi?.getVariantMinimumQuantity) return 1;
    return CartApi.getVariantMinimumQuantity(source);
  }

  canVariantMeetMinimumQuantity(source) {
    if (!window.CartApi?.canVariantMeetMinimumQuantity) return true;
    return CartApi.canVariantMeetMinimumQuantity(source);
  }

  isVariantPurchasable(source) {
    return this.getVariantAvailability(source) && this.canVariantMeetMinimumQuantity(source);
  }

  syncFormVariantMetadata(form, source) {
    const attributeMap = [
      ['data-variant-title', this.getVariantSourceValue(source, ['data-variant-title'], ['variantTitle'])],
      ['data-option-1', this.getVariantSourceValue(source, ['data-option-1'], ['option1'])],
      ['data-option-2', this.getVariantSourceValue(source, ['data-option-2'], ['option2'])],
      ['data-option-3', this.getVariantSourceValue(source, ['data-option-3'], ['option3'])]
    ];

    attributeMap.forEach(([name, value]) => {
      if (!value) {
        form.removeAttribute(name);
        return;
      }

      form.setAttribute(name, value);
    });
  }

  ensureQuantityInput(form) {
    let quantityInput = form.querySelector('input[name="quantity"]');

    if (quantityInput) return quantityInput;

    quantityInput = document.createElement('input');
    quantityInput.type = 'hidden';
    quantityInput.name = 'quantity';
    quantityInput.value = '1';
    form.appendChild(quantityInput);
    return quantityInput;
  }

  handleVariantTriggerClick(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    if (trigger?.disabled) return;
    this.selectVariantById(trigger.dataset.variantId);
  }

  handleVariantSelectChange() {
    const variantOption = this.getSelectedVariantOption();
    if (!variantOption) return;

    this.hideHoverImage();
    this.syncVariantState(variantOption);
  }

  handleCardMouseEnter() {
    this.showHoverImage();
  }

  handleCardMouseLeave() {
    this.hideHoverImage();
  }

  handleCardFocusIn() {
    this.showHoverImage();
  }

  handleCardFocusOut(event) {
    if (event.relatedTarget && this.imageHoverTarget?.contains(event.relatedTarget)) return;
    this.hideHoverImage();
  }

  handleWindowLoad() {
    this.observeHoverImageViewport();
  }

  handleHoverImageIntersection(entries) {
    const isVisible = entries.some((entry) => entry.isIntersecting);
    if (!isVisible) return;

    this.destroyHoverImageObserver();
    this.queueHoverImagePreload();
  }

  selectVariantById(variantId) {
    if (!this.variantSelect || !variantId) return;

    const variantOption = Array.from(this.variantSelect.options).find((option) => {
      return option.value === variantId || option.dataset.variantId === variantId;
    });

    if (!variantOption || variantOption.disabled) return;

    this.variantSelect.value = variantOption.value;
    variantOption.selected = true;
    this.hideHoverImage();
    this.syncVariantState(variantOption);
  }

  syncVariantState(variantOption, { updateImage = this.enableVariantImageSwap } = {}) {
    if (updateImage) {
      this.updateImage(variantOption);
    }

    this.updatePrice(variantOption);
    this.updateForms(variantOption);
    this.updateVariantButtons(variantOption);
    this.updateVariantDropdown(variantOption);
  }

  updateImage(variantOption) {
    if (!this.enableVariantImageSwap || !this.imageElement) return;

    const variantImage = variantOption.dataset.variantImage || '';
    const variantImageAlt = variantOption.dataset.variantImageAlt || this.defaultImageState?.alt || '';

    if (variantImage) {
      this.imageElement.src = variantImage;
      this.imageElement.srcset = '';
      this.imageElement.alt = variantImageAlt;
      return;
    }

    if (!this.defaultImageState) return;

    this.imageElement.src = this.defaultImageState.src;
    this.imageElement.srcset = this.defaultImageState.srcset;
    this.imageElement.alt = this.defaultImageState.alt;
  }

  showHoverImage() {
    if (!this.hasHoverImageSupport || this.hoverImageFailed) return;

    this.isHoverImageActive = true;
    this.ensureHoverImage().then((hoverImage) => {
      if (!hoverImage || !this.isHoverImageActive) return;
      this.mediaElement?.classList.add('is-hover-image-visible');
    });
  }

  hideHoverImage() {
    this.isHoverImageActive = false;
    this.mediaElement?.classList.remove('is-hover-image-visible');
  }

  setupHoverImagePreload() {
    if (!this.hasHoverImageSupport || this.hoverImageElement || this.hoverImageFailed) return;

    if (document.readyState === 'complete') {
      this.observeHoverImageViewport();
      return;
    }

    window.addEventListener('load', this.handleWindowLoad, { once: true });
  }

  observeHoverImageViewport() {
    if (
      !this.hasHoverImageSupport ||
      this.hoverImageElement ||
      this.hoverImageFailed ||
      !('IntersectionObserver' in window)
    ) {
      return;
    }

    if (this.hoverImageObserver) return;

    this.hoverImageObserver = new IntersectionObserver(this.handleHoverImageIntersection, {
      threshold: 0.01
    });

    this.hoverImageObserver.observe(this);
  }

  destroyHoverImageObserver() {
    if (!this.hoverImageObserver) return;

    this.hoverImageObserver.disconnect();
    this.hoverImageObserver = null;
  }

  queueHoverImagePreload() {
    if (!this.hasHoverImageSupport || this.hoverImageElement || this.hoverImageFailed) return;
    if (this.hoverImageIdleCallback || this.hoverImagePreloadTimer) return;

    const preload = () => {
      this.hoverImageIdleCallback = null;
      this.hoverImagePreloadTimer = null;
      this.ensureHoverImage();
    };

    if ('requestIdleCallback' in window) {
      this.hoverImageIdleCallback = window.requestIdleCallback(preload, { timeout: 1200 });
      return;
    }

    this.hoverImagePreloadTimer = window.setTimeout(preload, 150);
  }

  cancelHoverImagePreload() {
    if (this.hoverImageIdleCallback && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.hoverImageIdleCallback);
    }

    if (this.hoverImagePreloadTimer) {
      window.clearTimeout(this.hoverImagePreloadTimer);
    }

    this.hoverImageIdleCallback = null;
    this.hoverImagePreloadTimer = null;
  }

  ensureHoverImage() {
    if (!this.hasHoverImageSupport || this.hoverImageFailed) {
      return Promise.resolve(null);
    }

    if (this.hoverImageElement) {
      return this.hoverImageLoadPromise || Promise.resolve(this.hoverImageElement);
    }

    const hoverImage = document.createElement('img');
    const width = this.imageElement?.getAttribute('width');
    const height = this.imageElement?.getAttribute('height');

    hoverImage.className = 'product-card-hover-image';
    hoverImage.alt = this.hoverImageAlt;
    hoverImage.decoding = 'async';
    hoverImage.loading = 'eager';
    hoverImage.setAttribute('aria-hidden', 'true');

    if (width) {
      hoverImage.setAttribute('width', width);
    }

    if (height) {
      hoverImage.setAttribute('height', height);
    }

    this.hoverImageElement = hoverImage;
    this.hoverImageLoadPromise = new Promise((resolve) => {
      hoverImage.addEventListener('load', () => resolve(hoverImage), { once: true });
      hoverImage.addEventListener('error', () => {
        this.hoverImageFailed = true;
        this.mediaElement?.classList.remove('has-hover-image');
        hoverImage.remove();
        this.hoverImageElement = null;
        this.hoverImageLoadPromise = null;
        resolve(null);
      }, { once: true });
    });

    this.mediaElement.append(hoverImage);
    hoverImage.src = this.hoverImageSrc;

    return this.hoverImageLoadPromise;
  }

  updatePrice(variantOption) {
    if (!this.priceElement) return;

    const price = Number.parseInt(variantOption.dataset.variantPrice, 10) || 0;
    const comparePrice = Number.parseInt(variantOption.dataset.variantComparePrice, 10);
    const priceFormatted =
      variantOption.dataset.variantPriceFormatted || this.formatMoney(price);
    const compareFormatted =
      variantOption.dataset.variantComparePriceFormatted || this.formatMoney(comparePrice);
    const hasComparePrice = Number.isFinite(comparePrice) && comparePrice > price;

    this.priceElement.textContent = priceFormatted;

    if (!this.compareElement) return;

    this.compareElement.hidden = !hasComparePrice;
    if (hasComparePrice && this.compareValueElement) {
      this.compareValueElement.textContent = compareFormatted;
    }
  }

  updateForms(variantSource) {
    const variantId = this.getVariantId(variantSource);
    const available = this.isVariantPurchasable(variantSource);
    const quantity = this.getVariantMinimumQuantity(variantSource);

    this.forms.forEach((form) => {
      const action = form.dataset.productCardAction || 'cart';
      const idInput = form.querySelector('input[name="id"]');
      const submitBtn = form.querySelector('button[type="submit"]');
      const quantityInput = this.ensureQuantityInput(form);

      if (idInput) {
        idInput.value = variantId;
      }

      quantityInput.value = `${quantity}`;

      form.dataset.variantId = variantId;
      form.dataset.variantAvailable = this.getVariantAvailability(variantSource) ? 'true' : 'false';
      form.dataset.inventoryQuantity = this.getVariantSourceValue(
        variantSource,
        ['data-variant-inventory', 'data-inventory-quantity'],
        ['variantInventory', 'inventoryQuantity']
      ) || '0';
      form.dataset.inventoryManagement = this.getVariantSourceValue(
        variantSource,
        ['data-variant-inventory-management', 'data-inventory-management'],
        ['variantInventoryManagement', 'inventoryManagement']
      );
      form.dataset.inventoryPolicy = this.getVariantSourceValue(
        variantSource,
        ['data-variant-inventory-policy', 'data-inventory-policy'],
        ['variantInventoryPolicy', 'inventoryPolicy']
      );
      this.syncFormVariantMetadata(form, variantSource);
      form.hidden = action === 'checkout' && !available;

      if (!submitBtn) return;

      const text = submitBtn.querySelector('.btn-text');
      const availableText = submitBtn.dataset.availableText || text?.textContent || '';
      const soldOutText = submitBtn.dataset.soldOutText || availableText;

      if (text) {
        text.textContent = available ? availableText : soldOutText;
      }

      submitBtn.disabled = !available;
    });
  }

  updateVariantButtons(variantOption) {
    const selectedId = variantOption.dataset.variantId || variantOption.value;
    let hasVisibleActive = false;

    this.variantTriggers.forEach((trigger) => {
      const isActive = trigger.dataset.variantId === selectedId;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      hasVisibleActive = hasVisibleActive || isActive;
    });

    if (this.moreLink) {
      this.moreLink.classList.toggle('is-active', !hasVisibleActive);
    }
  }

  formatMoney(cents) {
    if (window.Money?.format) {
      return window.Money.format(cents);
    }

    const safeValue = Number.isFinite(cents) ? cents : 0;
    const symbol = window.theme?.currencySymbol || '$';
    return `${symbol}${(safeValue / 100).toFixed(2)}`;
  }

  async handleFormSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const submitBtn = form.querySelector('button[type="submit"]');

    if (!submitBtn || submitBtn.disabled) return;

    submitBtn.setAttribute('disabled', 'disabled');
    submitBtn.classList.add('loading');

    const formData = new FormData(form);
    const variantData = this.getSelectedVariantOption() || form;
    const action = form.dataset.productCardAction || 'cart';

    try {
      if (action === 'checkout') {
        await this.buyNow(formData, variantData);
        return;
      }

      const cartResponse = await this.submitToCart(formData, variantData);
      const cart = window.CartApi?.refreshCart
        ? await CartApi.refreshCart(cartResponse)
        : cartResponse;

      document.dispatchEvent(new CustomEvent('cart:added', { detail: cart || cartResponse }));

      if (!window.SimpleHeaderCart?.open) {
        const cartTrigger =
          document.getElementById('simple-header-cart-trigger') ||
          document.getElementById('custom-header-cart-trigger');
        if (cartTrigger) {
          cartTrigger.click();
        }
      }
    } catch (error) {
      if (window.CartApi?.showToast) {
        CartApi.showToast(error.message, true);
      } else {
        console.error(error);
      }
    } finally {
      submitBtn.classList.remove('loading');

      const selectedVariant = this.getSelectedVariantOption() || form;
      const isAvailable = this.isVariantPurchasable(selectedVariant);

      if (isAvailable) {
        submitBtn.removeAttribute('disabled');
      } else {
        submitBtn.setAttribute('disabled', 'disabled');
      }
    }
  }

  async submitToCart(formData, variantData) {
    if (window.CartApi?.addToCart) {
      return CartApi.addToCart(formData, { variantData });
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Could not add to cart');
    }

    return response.json();
  }

  setupVariantDropdown() {
    if (!this.variantSelect || this.variantDropdownButton) return;

    const container = this.variantSelect.closest('.product-card4__variants') || this.variantSelect.parentElement;
    if (!container) return;

    this.variantDropdownId = `card4-variant-${Math.random().toString(36).slice(2, 10)}`;
    this.variantDropdownContainer = container;
    this.isVariantDropdownOpen = false;
    this.variantDropdownActiveIndex = -1;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'product-card4__variant-button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-label', this.variantSelect.getAttribute('aria-label') || 'Variant');
    button.id = `${this.variantDropdownId}-button`;

    const label = document.createElement('span');
    label.className = 'product-card4__variant-button-label';
    button.appendChild(label);

    const panel = document.createElement('ul');
    panel.className = 'product-card4__variant-list';
    panel.setAttribute('role', 'listbox');
    panel.setAttribute('tabindex', '-1');
    panel.id = `${this.variantDropdownId}-list`;
    panel.hidden = true;
    button.setAttribute('aria-controls', panel.id);

    Array.from(this.variantSelect.options).forEach((option, index) => {
      const item = document.createElement('li');
      item.className = 'product-card4__variant-option';
      item.id = `${this.variantDropdownId}-option-${index}`;
      item.setAttribute('role', 'option');
      item.dataset.value = option.value;
      item.dataset.index = `${index}`;
      item.textContent = option.textContent.trim();
      item.setAttribute('aria-selected', option.selected ? 'true' : 'false');

      if (option.disabled) {
        item.classList.add('is-disabled');
        item.setAttribute('aria-disabled', 'true');
      }

      panel.appendChild(item);
    });

    this.variantSelect.classList.add('product-card4__variant-select--enhanced');
    this.variantSelect.setAttribute('tabindex', '-1');
    this.variantSelect.setAttribute('aria-hidden', 'true');
    container.classList.add('product-card4__variants--enhanced');
    container.appendChild(button);

    this.variantDropdownButton = button;
    this.variantDropdownLabel = label;
    this.variantDropdownPanel = panel;
    this.variantDropdownOptions = Array.from(panel.children);

    button.addEventListener('click', this.handleDropdownButtonClick);
    button.addEventListener('keydown', this.handleDropdownButtonKeydown);
    panel.addEventListener('click', this.handleDropdownPanelClick);
    panel.addEventListener('keydown', this.handleDropdownPanelKeydown);

    this.updateVariantDropdown(this.getSelectedVariantOption());
  }

  destroyVariantDropdown() {
    if (!this.variantDropdownButton) return;

    this.closeVariantDropdown({ focusButton: false });

    this.variantDropdownButton.removeEventListener('click', this.handleDropdownButtonClick);
    this.variantDropdownButton.removeEventListener('keydown', this.handleDropdownButtonKeydown);
    this.variantDropdownPanel?.removeEventListener('click', this.handleDropdownPanelClick);
    this.variantDropdownPanel?.removeEventListener('keydown', this.handleDropdownPanelKeydown);

    this.variantDropdownButton.remove();
    this.variantDropdownPanel?.remove();

    this.variantSelect?.classList.remove('product-card4__variant-select--enhanced');
    this.variantSelect?.removeAttribute('tabindex');
    this.variantSelect?.removeAttribute('aria-hidden');
    this.variantDropdownContainer?.classList.remove('product-card4__variants--enhanced');

    this.variantDropdownButton = null;
    this.variantDropdownPanel = null;
    this.variantDropdownLabel = null;
    this.variantDropdownOptions = [];
  }

  updateVariantDropdown(variantOption) {
    if (!this.variantDropdownButton || !this.variantSelect) return;

    const selectedIndex = this.variantSelect.selectedIndex;
    const selected = variantOption || this.getSelectedVariantOption();

    if (this.variantDropdownLabel) {
      this.variantDropdownLabel.textContent = selected ? selected.textContent.trim() : '';
    }

    this.variantDropdownOptions?.forEach((item, index) => {
      const isSelected = index === selectedIndex;
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      item.classList.toggle('is-selected', isSelected);
    });
  }

  handleDropdownButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isVariantDropdownOpen) {
      this.closeVariantDropdown();
    } else {
      this.openVariantDropdown();
    }
  }

  handleDropdownButtonKeydown(event) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Spacebar'].includes(event.key)) {
      event.preventDefault();
      this.openVariantDropdown();
    }
  }

  handleDropdownPanelClick(event) {
    const item = event.target.closest('.product-card4__variant-option');
    if (!item || item.classList.contains('is-disabled')) return;

    event.preventDefault();
    this.commitVariantDropdownOption(Number(item.dataset.index));
  }

  handleDropdownPanelKeydown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.closeVariantDropdown();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.moveVariantDropdownActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveVariantDropdownActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.setVariantDropdownActive(this.findEnabledOptionIndex(0, 1));
        break;
      case 'End':
        event.preventDefault();
        this.setVariantDropdownActive(this.findEnabledOptionIndex(this.variantDropdownOptions.length - 1, -1));
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        this.commitVariantDropdownOption(this.variantDropdownActiveIndex);
        break;
      case 'Tab':
        this.closeVariantDropdown({ focusButton: false });
        break;
      default:
        break;
    }
  }

  handleDocumentPointerDown(event) {
    if (!this.isVariantDropdownOpen) return;
    if (this.variantDropdownPanel?.contains(event.target)) return;
    if (this.variantDropdownButton?.contains(event.target)) return;

    this.closeVariantDropdown({ focusButton: false });
  }

  handleDropdownReposition() {
    if (!this.isVariantDropdownOpen) return;
    this.positionVariantDropdown();
  }

  openVariantDropdown() {
    if (!this.variantDropdownPanel || this.isVariantDropdownOpen) return;

    document.body.appendChild(this.variantDropdownPanel);
    this.variantDropdownPanel.hidden = false;
    this.variantDropdownButton.setAttribute('aria-expanded', 'true');
    this.variantDropdownButton.classList.add('is-open');
    this.isVariantDropdownOpen = true;

    this.positionVariantDropdown();

    document.addEventListener('pointerdown', this.handleDocumentPointerDown, true);
    window.addEventListener('scroll', this.handleDropdownReposition, true);
    window.addEventListener('resize', this.handleDropdownReposition);

    const selectedIndex = this.variantSelect?.selectedIndex ?? 0;
    const activeIndex = this.variantDropdownOptions[selectedIndex]?.classList.contains('is-disabled')
      ? this.findEnabledOptionIndex(0, 1)
      : selectedIndex;

    this.variantDropdownPanel.focus({ preventScroll: true });
    this.setVariantDropdownActive(activeIndex);
  }

  closeVariantDropdown({ focusButton = true } = {}) {
    if (!this.variantDropdownPanel || !this.isVariantDropdownOpen) return;

    this.variantDropdownPanel.hidden = true;
    this.variantDropdownPanel.remove();
    this.variantDropdownButton?.setAttribute('aria-expanded', 'false');
    this.variantDropdownButton?.classList.remove('is-open');
    this.isVariantDropdownOpen = false;

    document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
    window.removeEventListener('scroll', this.handleDropdownReposition, true);
    window.removeEventListener('resize', this.handleDropdownReposition);

    if (focusButton) {
      this.variantDropdownButton?.focus({ preventScroll: true });
    }
  }

  positionVariantDropdown() {
    if (!this.variantDropdownButton || !this.variantDropdownPanel) return;

    const panel = this.variantDropdownPanel;
    const rect = this.variantDropdownButton.getBoundingClientRect();
    const gap = 4;
    const margin = 8;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom - gap - margin;
    const spaceAbove = rect.top - gap - margin;

    panel.style.width = `${rect.width}px`;
    panel.style.left = `${rect.left}px`;

    const naturalHeight = panel.scrollHeight;
    const openUpwards = spaceBelow < Math.min(naturalHeight, 180) && spaceAbove > spaceBelow;
    const available = Math.max(120, openUpwards ? spaceAbove : spaceBelow);

    panel.style.maxHeight = `${Math.min(260, available)}px`;

    if (openUpwards) {
      panel.style.top = 'auto';
      panel.style.bottom = `${viewportHeight - rect.top + gap}px`;
    } else {
      panel.style.bottom = 'auto';
      panel.style.top = `${rect.bottom + gap}px`;
    }
  }

  findEnabledOptionIndex(startIndex, step) {
    const options = this.variantDropdownOptions || [];

    for (let index = startIndex; index >= 0 && index < options.length; index += step) {
      if (!options[index].classList.contains('is-disabled')) return index;
    }

    return -1;
  }

  moveVariantDropdownActive(step) {
    const options = this.variantDropdownOptions || [];
    if (!options.length) return;

    const start =
      this.variantDropdownActiveIndex < 0 ? (step > 0 ? -1 : options.length) : this.variantDropdownActiveIndex;
    const nextIndex = this.findEnabledOptionIndex(start + step, step);

    if (nextIndex >= 0) {
      this.setVariantDropdownActive(nextIndex);
    }
  }

  setVariantDropdownActive(index) {
    const options = this.variantDropdownOptions || [];
    if (index < 0 || index >= options.length) return;

    options.forEach((item) => item.classList.remove('is-active'));

    const item = options[index];
    item.classList.add('is-active');
    item.scrollIntoView({ block: 'nearest' });

    this.variantDropdownActiveIndex = index;
    this.variantDropdownPanel?.setAttribute('aria-activedescendant', item.id);
  }

  commitVariantDropdownOption(index) {
    const options = this.variantDropdownOptions || [];
    const item = options[index];
    if (!item || item.classList.contains('is-disabled')) return;

    this.closeVariantDropdown();

    if (this.variantSelect && this.variantSelect.selectedIndex !== index) {
      this.variantSelect.selectedIndex = index;
      this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.updateVariantDropdown(this.getSelectedVariantOption());
    }
  }

  async buyNow(formData, variantData) {
    await this.submitToCart(formData, variantData);

    const rootPath = window.Shopify?.routes?.root || '/';
    window.location.assign(new URL('checkout', `${window.location.origin}${rootPath}`).toString());
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
